<template>
  <div>
    <div class="sidepanel__block">
      <h6 class="subtitle subtitle--center">{{ t('pages') }}</h6>
      <hr />
      <div class="pages-manager__new">
        <button
          :disabled="!canAddNewDescription"
          class="button button--light-turquoise button--square no-space-out"
          @click.prevent.stop="newDescription"
        >
          <span class="button__holder">
            <svg class="icon button__icon icon--white">
              <use :xlink:href="iconUrl('icon-plus')"></use>
            </svg>
            <span class="button__text">{{ t('new_tab') }}</span>
          </span>
        </button>
      </div>
      <draggable
        tag="ul"
        class="list list--bordered"
        :list="eventDescriptions"
        v-bind="{ handle: '.pages-manager__drag', ghostClass: 'pages-manager__ghost', animation: 150 }"
      >
        <li
          class="list__item list__item--tighter"
          :class="{ 'is-hidden': isDeleted(field) }"
          :key="field.uid"
          v-for="(field, index) in eventDescriptions"
        >
          <input
            type="hidden"
            :value="field.id"
            :name="fieldName(index, 'id')"
            v-if="field['id']"
          />
          <input type="hidden" :value="field.name" :name="fieldName(index, 'name')" />
          <input type="hidden" :value="field.visible" :name="fieldName(index, 'visible')" />
          <input type="hidden" :value="index * 1000" :name="fieldName(index, 'row_order')" />
          <input
            type="hidden"
            :value="field['_destroy']"
            :name="fieldName(index, '_destroy')"
            v-if="field['_destroy'] == '1'"
          />

          <span class="list__block list__block--tighter">
            <span class="pages-manager__drag">
              <svg class="icon icon--tiny icon--slategray">
                <use :xlink:href="iconUrl('icon-move')"></use>
              </svg>
            </span>
            <span class="pages-manager__number">{{ index + 1 }}</span>
            <div class="list__content has-full-width pages-manager__content">
              <!-- Inline rename: clicking the name swaps it for an input.
                   Enter / blur commit the new name into the row (the hidden
                   `name` field above is submitted by the panel's Save button,
                   reusing the existing update API); Escape cancels. -->
              <input
                v-if="editingUid === field.uid"
                ref="editInput"
                type="text"
                class="input input--full"
                :maxlength="nameMaxLength"
                v-model="editingValue"
                @click.stop
                @keydown.enter.prevent.stop="commitEditing(field)"
                @keydown.esc.prevent.stop="cancelEditing"
                @blur="commitEditing(field)"
              />
              <span
                v-else
                class="list__text pages-manager__name"
                :class="{ 'pages-manager__name--hidden': !field.visible }"
                :title="t('edit_tab_name')"
                @click.prevent.stop="startEditing(field)"
              >{{ field.name }}</span>
              <!-- Visibility toggle: checkbox + eye kept together so the label
                   click still toggles and `:checked ~ .switch__icon` still
                   colors the icon. Pushed to the right of the name (beside the
                   overflow menu) via margin-left: auto. -->
              <label class="switch switch--start pages-manager__visibility" onclick="">
                <input type="checkbox" class="switch__el" v-model="field.visible" />
                <svg class="icon icon--tiny icon--gray switch__icon">
                  <use :xlink:href="iconUrl('icon-eye')"></use>
                </svg>
              </label>
            </div>
            <div class="dropdown dropdown--floating dropdown--table">
              <div class="dropdown__holder">
                <a href="#" class="button button--circle button--transparent no-space-out noclick">
                  <svg class="icon icon--tiny icon--slategray dropdown__icon">
                    <use :xlink:href="iconUrl('icon-dots-vertical')"></use>
                  </svg>
                </a>
                <ul
                  class="
                    dropdown__list
                    dropdown__list--floating
                    dropdown__list--medium
                    dropdown__list--right
                  "
                >
                  <li class="dropdown__item">
                    <a href="#" class="dropdown__link" @click.prevent.stop="startEditing(field)">
                      <span class="dropdown__legend">{{ t('rename') }}</span>
                    </a>
                  </li>
                  <li class="dropdown__item">
                    <a
                      href="#"
                      class="dropdown__link"
                      @click.prevent.stop="deleteDescription(index)"
                    >
                      <span class="dropdown__legend is-danger">{{ t('delete_tab') }}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </span>
        </li>
      </draggable>
    </div>
  </div>
</template>

<script>
import MobileDropdown from '../../application/app/mobile_dropdown';
import draggable from 'vuedraggable';
import I18n from 'i18n-js/index.js.erb';
const t = (key, opts) =>
  I18n.t(key, {
    scope: 'js.edit_description_tabs',
    ...opts,
  });

export default {
  components: {
    draggable,
  },

  props: {
    initialEventDescriptions: {
      type: Array,
      required: true,
    },
    svgIconsUrl: {
      type: String,
      required: true,
    },
    descriptionTabsLimit: {
      type: Number,
      required: true,
    },
    nameMaxLength: {
      type: Number,
      required: true,
    },
  },

  data() {
    this.initialEventDescriptions.forEach((description) => {
      description['_destroy'] = '0';
      description['uid'] = description.id;
    });
    return {
      eventDescriptions: this.initialEventDescriptions,
      editingUid: null,
      editingValue: '',
    };
  },

  computed: {
    canAddNewDescription: (vm) => vm.undeletedDescripptions.length < vm.descriptionTabsLimit,
    undeletedDescripptions: (vm) => vm.eventDescriptions.filter(vm.notDeleted),
  },

  methods: {
    t,

    iconUrl(name) {
      return `${this.svgIconsUrl}#${name}`;
    },

    fieldName(index, fieldName) {
      return `event[event_descriptions_attributes][${index}][${fieldName}]`;
    },

    startEditing(field) {
      this.editingUid = field.uid;
      this.editingValue = field.name;
      this.$nextTick(() => {
        const ref = this.$refs.editInput;
        const input = Array.isArray(ref) ? ref[0] : ref;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },

    commitEditing(field) {
      // Guard against a trailing blur firing after Enter/Escape already ended
      // the edit (the input is removed from the DOM, which can emit blur).
      if (this.editingUid !== field.uid) return;
      const name = this.editingValue.trim();
      if (name) {
        field.name = name;
      }
      this.editingUid = null;
      this.editingValue = '';
    },

    cancelEditing() {
      this.editingUid = null;
      this.editingValue = '';
    },

    newDescription() {
      const nextTabCounter = this.eventDescriptions.length + 1;
      const description = {
        name: `${t('tab')} ${nextTabCounter}`,
        uid: this.randomInt(),
        _destroy: '0',
        visible: true,
      };
      this.eventDescriptions.push(description);
      this.$nextTick(() => {
        const $sidepanel = $('aside.sidepanel');
        MobileDropdown.hookEvents($sidepanel);
        // Smooth create flow: drop straight into inline editing of the new page.
        this.startEditing(description);
      });
    },

    deleteDescription(index) {
      const description = this.eventDescriptions[index];
      description['_destroy'] = '1';
    },

    isDeleted(description) {
      return description['_destroy'] === '1';
    },

    notDeleted(description) {
      return !this.isDeleted(description);
    },

    randomInt() {
      return Math.floor(Math.random() * 999999) + 100000;
    },
  },
};
</script>
